import { ToolsClass } from '@/base/class/tools.class'
import { MOBILE_EVENT_NAMES } from '@/base/const/mobileEvents.const'
import {
  TRX_CLIENT_ENV,
  TRX_DIMENSION,
  TRX_ERROR_HTTP_PREFIX,
  TRX_ERROR_NO_RESPONSE,
  TRX_EVENT_CATEGORY,
  TRX_LEAVING_STEPS,
  TRX_ORDER_CURRENCY,
  TRX_QUEUE_LIMIT,
  TRX_STEP,
  TRX_TRACKER_POLL_MS,
  TRX_TRACKER_WAIT_MS,
} from '@/base/const/transactionTracking.const'

/**
 * PEP-1380 — jedyne wejście do drugiego projektu Matomo, w którym jednostką analizy jest transakcja
 * (`idTransRemote` = User ID), a nie przeglądarka. Projekt 47/48 (`MatomoClass`, `_paq`) działa obok
 * bez zmian. → `doc/dokumentacja/matomo.md` §10
 *
 * Zasady, których nie wolno poluzować:
 * - NIC nie wychodzi przed `identifyTransaction()`. Zdarzenia czekają w buforze, a jeśli dokument nigdy
 *   nie pozna ID (`/secure-back`, `/card-status` bez stanu), bufor przepada. Pierwsze żądanie bez
 *   User ID założyłoby w Matomo osobnego odwiedzającego i ścieżka transakcji by pękła.
 * - Tracker to osobna instancja `Matomo.getTracker()`, NIGDY `_paq` — `_paq` rozsyła polecenia do
 *   wszystkich trackerów, więc `setUserId` trafiłby też do projektu 47.
 * - Adres wysyłany do Matomo to zawsze nazwa trasy (`/web/<kontekst>/<Trasa>`), nigdy `location.href`
 *   — ścieżki niosą `apiToken`, `Hash` i `continueHash`. Referrer obcinamy do originu z tego samego powodu.
 * - Moduł nie rzuca i niczego nie opóźnia. Brak `piwik.js` (blokery reklam) = no-op, bez raportu do
 *   Sentry: to stan środowiska użytkownika, nie awaria.
 */

const TRACKER_URL = 'https://piwik.blue.pl/piwik.php'
const LOG_PREFIX = '[PEP-1380 transakcje]'
const UNKNOWN_ROUTE = 'nieznana'

let config = null
let tracker = null
let trackerRequested = false
let identity = null
let queue = []
let lastRouteName = UNKNOWN_ROUTE
let referrerSent = false
let leaveListenerAttached = false
let trackedErrors = new Set()

function debugLog(message, details) {
  if (!ToolsClass.isDebugEnabled()) {
    return
  }
  console.log(LOG_PREFIX, message, details ?? '')
}

function readSiteId() {
  const siteId = import.meta.env.VITE_APP_MATOMO_TRX_SITE_ID
  return siteId ? String(siteId) : null
}

function detectClientEnv() {
  if (ToolsClass.isAndroid()) {
    return TRX_CLIENT_ENV.sdkAndroid
  }
  if (ToolsClass.isIOS(MOBILE_EVENT_NAMES.transactionFinishCallback)) {
    return TRX_CLIENT_ENV.sdkIos
  }
  // Android WebView bez naszego SDK (aplikacja partnera) — ten sam znacznik co w analizie PEP-104
  if (/; wv\)/.test(navigator.userAgent || '')) {
    return TRX_CLIENT_ENV.webviewAndroid
  }
  return TRX_CLIENT_ENV.web
}

function buildUrl(routeName) {
  return `${window.location.origin}/web/${config.context}/${routeName}`
}

/** Sam origin poprzedniej strony (bank, sklep, ACS) — pełny adres bywa naszą trasą z tokenem. */
function referrerOrigin() {
  try {
    return document.referrer ? new URL(document.referrer).origin : ''
  } catch {
    return ''
  }
}

function compactDimensions(values) {
  const result = {}
  Object.entries(values).forEach(([id, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      result[`dimension${id}`] = String(value)
    }
  })
  return result
}

/** Numer zamówienia sklepu albo `null` — brak i same białe znaki nie są wartością, którą warto wysłać. */
function normalizeOrderId(orderId) {
  if (orderId === undefined || orderId === null) {
    return null
  }
  const value = String(orderId).trim()
  return value === '' ? null : value
}

function applyIdentity() {
  tracker.setUserId(identity.idTransRemote)
  tracker.setCustomDimension(TRX_DIMENSION.visitIdTransRemote, identity.idTransRemote)
  tracker.setCustomDimension(TRX_DIMENSION.actionIdTransRemote, identity.idTransRemote)
  if (identity.serviceId) {
    tracker.setCustomDimension(TRX_DIMENSION.serviceId, String(identity.serviceId))
  } else {
    tracker.deleteCustomDimension(TRX_DIMENSION.serviceId)
  }
  // Wymiar ustawiony na instancji trackera wychodzi z każdym kolejnym żądaniem, więc przy przepięciu
  // na transakcję bez numeru zamówienia trzeba go zdjąć — inaczej niosłaby numer poprzedniej (PEP-1420).
  if (identity.orderId) {
    tracker.setCustomDimension(TRX_DIMENSION.orderId, identity.orderId)
  } else {
    tracker.deleteCustomDimension(TRX_DIMENSION.orderId)
  }
}

function send(item) {
  tracker.setCustomUrl(buildUrl(item.routeName))
  // Referrer niesie tylko pierwsze żądanie dokumentu — dalsze odsłony SPA nie przyszły z banku
  tracker.setReferrerUrl(referrerSent ? '' : referrerOrigin())
  referrerSent = true

  if (item.kind === 'view') {
    tracker.setDocumentTitle(item.routeName)
    tracker.trackPageView(item.routeName, compactDimensions({ [TRX_DIMENSION.step]: TRX_STEP.view }))
  } else if (item.kind === 'order') {
    // `trackEcommerceOrder` nie przyjmuje `customData`, więc krok ustawiamy na czas jednego żądania.
    // Numer zamówienia = `idTransRemote`: Matomo przyjmuje jedno zamówienie o danym numerze w projekcie,
    // więc F5 na potwierdzeniu ani ponowny powrót nie zdublują przychodu.
    tracker.setCustomDimension(TRX_DIMENSION.step, TRX_STEP.order)
    tracker.trackEcommerceOrder(identity.idTransRemote, item.amount)
    tracker.deleteCustomDimension(TRX_DIMENSION.step)
  } else {
    const dimensions = compactDimensions({
      [TRX_DIMENSION.step]: item.step,
      [TRX_DIMENSION.payway]: item.payway,
      [TRX_DIMENSION.group]: item.group,
      [TRX_DIMENSION.state]: item.state,
    })
    if (TRX_LEAVING_STEPS.includes(item.step) && sendEventBeacon(item, dimensions)) {
      debugLog('wysłano (beacon)', { ...item, idTransRemote: identity.idTransRemote })
      return
    }
    tracker.trackEvent(TRX_EVENT_CATEGORY, item.step, item.name, undefined, dimensions)
  }
  debugLog('wysłano', { ...item, idTransRemote: identity.idTransRemote })
}

/**
 * Zdarzenie wysłane od razu, z pominięciem kolejki `piwik.js`.
 *
 * Tracker Matomo po PIERWSZYM żądaniu dokumentu odkłada każde kolejne o 800 ms (`setTimeout`,
 * w źródle `makeSureThereIsAGapAfterFirstTrackingRequestToPreventMultipleVisitorCreation`) — także
 * przy `alwaysUseSendBeacon`, bo beacon wychodzi dopiero z tego timera. Przy kanale wybranym przed
 * bramką `/start`/`/continue` oddaje gotowy formularz, `PaymentData` wysyła go od razu, a szybki bank
 * (symulator kanału 106) zabiera dokument przed upływem 800 ms: ginęły wtedy i `platnosc_zlecona`,
 * i `opuszczenie_dokumentu`, a w Matomo zostawał sam `widok` (pomiar STG 12.10.2026).
 *
 * `getRequest()` to publiczna metoda trackera — składa to samo żądanie co `trackEvent` (`idsite`,
 * User ID, `_id`, `pv_id`, wymiary ustawione na trackerze), tylko go nie wysyła. Wizytę wiąże User ID
 * = `idTransRemote`, więc ominięcie odstępu nie zakłada drugiego odwiedzającego.
 *
 * @param {object} item zdarzenie z bufora
 * @param {object} dimensions wymiary akcji (`dimension<N>`)
 * @returns {boolean} `false` = beacon niedostępny albo odrzucony; wołający wysyła zwykłą ścieżką
 */
function sendEventBeacon(item, dimensions) {
  if (typeof navigator.sendBeacon !== 'function' || typeof tracker.getRequest !== 'function') {
    return false
  }
  const params = { e_c: TRX_EVENT_CATEGORY, e_a: item.step, e_n: item.name, ca: '1', ...dimensions }
  const query = Object.entries(params)
    .filter(([, value]) => value !== undefined)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&')
  try {
    const request = tracker.getRequest(query)
    if (!request) {
      return false
    }
    return navigator.sendBeacon(
      TRACKER_URL,
      new Blob([request], { type: 'application/x-www-form-urlencoded; charset=UTF-8' })
    )
  } catch {
    return false
  }
}

function flush() {
  const pending = queue
  queue = []
  pending.forEach(send)
}

function onPageHide() {
  if (!tracker || !identity) {
    return
  }
  try {
    send({ kind: 'event', step: TRX_STEP.documentLeave, name: lastRouteName, routeName: lastRouteName })
  } catch {
    // tracker nie może zatrzymać zamykania strony
  }
}

function createTracker() {
  const instance = window.Matomo.getTracker(TRACKER_URL, config.siteId)
  // Wtyczka HSR podpina się pod KAŻDY tracker i pyta serwer o konfigurację z pełnym `location.href`
  instance.HeatmapSessionRecording?.disable?.()
  instance.disableCookies()
  // Wyjście do banku to `form.submit()` / zmiana `location` — zwykłe żądanie ginie przy nawigacji
  instance.alwaysUseSendBeacon?.()
  instance.setCustomDimension(TRX_DIMENSION.context, config.context)
  instance.setCustomDimension(TRX_DIMENSION.clientEnv, detectClientEnv())
  return instance
}

function onTrackerReady() {
  try {
    tracker = createTracker()
    applyIdentity()
    flush()
    if (!leaveListenerAttached) {
      window.addEventListener('pagehide', onPageHide)
      leaveListenerAttached = true
    }
  } catch (error) {
    tracker = null
    queue = []
    debugLog('nie udało się utworzyć trackera', error)
  }
}

/** `piwik.js` ładuje się async z `index.html` — tracker tworzymy dopiero przy identyfikacji. */
function requestTracker() {
  if (trackerRequested) {
    return
  }
  trackerRequested = true
  const startedAt = Date.now()
  const poll = () => {
    if (typeof window.Matomo?.getTracker === 'function') {
      onTrackerReady()
      return
    }
    if (Date.now() - startedAt >= TRX_TRACKER_WAIT_MS) {
      queue = []
      debugLog('brak piwik.js — zdarzenia porzucone')
      return
    }
    setTimeout(poll, TRX_TRACKER_POLL_MS)
  }
  poll()
}

function enqueue(item) {
  if (!config) {
    return
  }
  try {
    if (tracker && identity) {
      send(item)
      return
    }
    if (queue.length < TRX_QUEUE_LIMIT) {
      queue.push(item)
    }
  } catch (error) {
    debugLog('błąd wysyłki', error)
  }
}

/**
 * Włącza moduł dla dokumentu. Bez `VITE_APP_MATOMO_TRX_SITE_ID` (produkcja, tst, lokalnie) każda
 * funkcja tego pliku jest no-opem.
 *
 * @param {object} options
 * @param {string} options.context nazwa kontekstu bramki
 * @param {import('vue-router').Router} options.router
 */
export function initTransactionTracking({ context, router }) {
  const siteId = readSiteId()
  if (!siteId || config) {
    return
  }
  config = { siteId, context }

  router.afterEach((to) => {
    trackView(to)
    // Trasy z ID w ścieżce: continue, regulations-approval, visadata, wait, card-back
    if (to.params?.idTransRemote) {
      identifyTransaction(to.params.idTransRemote)
    }
  })
}

/**
 * Wiąże dokument z transakcją i wypuszcza bufor. Wołane wszędzie tam, gdzie ID staje się znane
 * (tabela w `doc/dokumentacja/matomo.md` §9). Ponowne wywołanie z tym samym ID tylko uzupełnia
 * `serviceId` i `orderId` (pierwsza znana wartość zostaje); inne ID przepina dalsze zdarzenia na nową
 * transakcję i NIE dziedziczy numeru zamówienia poprzedniej. Brak `orderId` nie blokuje wysyłki i nie
 * jest zastępowany niczym innym (`idTransRemote` też nie), a numer zamówienia znaczy coś tylko razem
 * z `serviceId` — sam bywa ten sam u różnych sklepów (PEP-1420).
 *
 * @param {string} idTransRemote
 * @param {object} [details]
 * @param {string|number} [details.serviceId]
 * @param {string|number} [details.orderId] numer zamówienia sklepu
 */
export function identifyTransaction(idTransRemote, { serviceId, orderId } = {}) {
  if (!config || !idTransRemote) {
    return
  }
  try {
    const id = String(idTransRemote)
    const normalizedOrderId = normalizeOrderId(orderId)
    if (identity?.idTransRemote === id) {
      const fillsServiceId = serviceId && !identity.serviceId
      const fillsOrderId = normalizedOrderId && !identity.orderId
      if (fillsServiceId) {
        identity.serviceId = serviceId
      }
      if (fillsOrderId) {
        identity.orderId = normalizedOrderId
      }
      if ((fillsServiceId || fillsOrderId) && tracker) {
        applyIdentity()
      }
      return
    }
    identity = { idTransRemote: id, serviceId: serviceId ?? null, orderId: normalizedOrderId }
    debugLog('identyfikacja', identity)
    if (tracker) {
      applyIdentity()
      flush()
      return
    }
    requestTracker()
  } catch (error) {
    debugLog('błąd identyfikacji', error)
  }
}

/**
 * Odsłona trasy — wołana z `router.afterEach`, poza modułem nie jest potrzebna.
 * @param {import('vue-router').RouteLocationNormalized} route
 */
export function trackView(route) {
  if (!config) {
    return
  }
  lastRouteName = String(route?.name || UNKNOWN_ROUTE)
  enqueue({ kind: 'view', routeName: lastRouteName })
}

/**
 * Krok biznesowy transakcji.
 *
 * @param {string} step wartość z `TRX_STEP`
 * @param {object} [details]
 * @param {string} [details.name] szczegół kroku (nazwa zdarzenia)
 * @param {string|number} [details.payway] `paywayID`
 * @param {string} [details.group] grupa w słowniku `MATOMO_PAYMENT_TYPE_GROUP`
 * @param {string} [details.state] stan transakcji znany frontowi
 */
export function trackStep(step, { name, payway, group, state } = {}) {
  if (!config) {
    return
  }
  enqueue({
    kind: 'event',
    step,
    name: name !== undefined && name !== null ? String(name) : undefined,
    payway,
    group,
    state,
    routeName: lastRouteName,
  })
}

/**
 * Widok błędu — wyłącznie w dokumencie, który zna już `idTransRemote`. W odróżnieniu od pozostałych
 * kroków NIE czeka w buforze na identyfikację: błąd `/start` nie niesie danych transakcji, więc ID nie
 * przyjdzie nigdy, a zdarzenie bez ID nie da się powiązać z transakcją. Na `/continue` ID jest
 * z trasy, więc błąd API (wygasły link, `GENERAL_ERROR`) zostaje przypisany do transakcji.
 * Ten sam kod dla tej samej transakcji idzie raz na dokument (ponowny montaż widoku, zmiana języka).
 * Dane błędu bez kodu dostają nazwę zastępczą (`HTTP_<status>` albo `BRAK_ODPOWIEDZI`).
 *
 * @param {string} [errorCode] kod błędu z magazynu (`getErrorDataCode`)
 * @param {number|null} [httpStatus] status odpowiedzi z magazynu (`getErrorDataHttpStatus`)
 */
export function trackErrorView(errorCode, httpStatus = null) {
  if (!config || !identity) {
    return
  }
  const name =
    errorCode || (typeof httpStatus === 'number' ? `${TRX_ERROR_HTTP_PREFIX}${httpStatus}` : TRX_ERROR_NO_RESPONSE)
  const key = `${identity.idTransRemote}|${name}`
  if (trackedErrors.has(key)) {
    return
  }
  trackedErrors.add(key)
  trackStep(TRX_STEP.error, { name })
}

/**
 * Zamówienie ecommerce dla transakcji opłaconej — wołające miejsce odpowiada za stan (pozytywny wynik).
 * Kwotą jest `payAmount` (ta sama, co w bazie). Inna waluta niż `TRX_ORDER_CURRENCY` albo kwota
 * nieliczbowa = brak zamówienia; wynik i tak opisuje krok `wynik`.
 *
 * @param {object} transaction
 * @param {number|string} transaction.amount `payAmount`
 * @param {string} transaction.currency `payAmountCurrency`
 */
export function trackPaidOrder({ amount, currency } = {}) {
  if (!config || currency !== TRX_ORDER_CURRENCY) {
    return
  }
  const value = Number(amount)
  if (!Number.isFinite(value) || value <= 0) {
    return
  }
  enqueue({ kind: 'order', amount: value, routeName: lastRouteName })
}

/** Wyłącznie dla testów jednostkowych — stan modułu żyje przez cały dokument. */
export function resetTransactionTrackingForTests() {
  if (leaveListenerAttached) {
    window.removeEventListener('pagehide', onPageHide)
  }
  config = null
  tracker = null
  trackerRequested = false
  identity = null
  queue = []
  lastRouteName = UNKNOWN_ROUTE
  referrerSent = false
  leaveListenerAttached = false
  trackedErrors = new Set()
}
